import { supabase, type CalendarProvider, type CalendarConnectionState } from '@saas-boilerplate/shared';

function normalizeAuthProvider(authProvider?: string | null): CalendarProvider | null {
  if (!authProvider) return null;
  const p = String(authProvider).toLowerCase();
  if (p === 'google') return 'google';
  if (p === 'azure') return 'microsoft';
  return null;
}

// Supabase only exposes provider_token / provider_refresh_token on the session immediately after
// the OAuth callback - they're dropped once the JWT refreshes. We stash them at the SIGNED_IN event
// so the calendar connection can still be bootstrapped once a workspace is selected (which can
// happen several navigations later). Cleared after a successful upsert.
const PROVIDER_TOKEN_STASH_KEY = 'upstand.oauth.providerTokens';
type StashedTokens = { provider: string | null; access_token: string | null; refresh_token: string | null; email: string | null };

export function stashOAuthProviderTokens(session: {
  provider_token?: string | null;
  provider_refresh_token?: string | null;
  user?: { app_metadata?: { provider?: string }; email?: string | null } | null;
} | null): void {
  if (!session) return;
  const access = session.provider_token ?? null;
  const refresh = session.provider_refresh_token ?? null;
  if (!access && !refresh) return; // nothing worth stashing
  try {
    sessionStorage.setItem(
      PROVIDER_TOKEN_STASH_KEY,
      JSON.stringify({
        provider: session.user?.app_metadata?.provider ?? null,
        access_token: access,
        refresh_token: refresh,
        email: session.user?.email ?? null,
      } as StashedTokens),
    );
  } catch {
    /* sessionStorage unavailable - non-fatal */
  }
}

function readStashedProviderTokens(): StashedTokens | null {
  try {
    const raw = sessionStorage.getItem(PROVIDER_TOKEN_STASH_KEY);
    return raw ? (JSON.parse(raw) as StashedTokens) : null;
  } catch {
    return null;
  }
}

function clearStashedProviderTokens(): void {
  try {
    sessionStorage.removeItem(PROVIDER_TOKEN_STASH_KEY);
  } catch {
    /* non-fatal */
  }
}

/**
 * All calendar-capable providers linked to the signed-in user, in preference order.
 *
 * Supabase stores the *first* identity in `app_metadata.provider` and the full set in
 * `app_metadata.providers`. A user who signed up with email/password and later linked Microsoft
 * keeps `provider: "email"` while `providers: ["email", "azure"]` - so reading the single
 * `provider` field alone would miss the linked calendar account entirely. We scan both.
 */
export async function getAuthCalendarProviders(): Promise<CalendarProvider[]> {
  const { data: userRes, error } = await supabase.auth.getUser();
  if (error) return [];
  const meta = userRes.user?.app_metadata as { provider?: string; providers?: string[] } | undefined;
  const ordered: string[] = [];
  if (meta?.provider) ordered.push(meta.provider);
  if (Array.isArray(meta?.providers)) {
    for (const p of meta.providers) if (!ordered.includes(p)) ordered.push(p);
  }
  const result: CalendarProvider[] = [];
  for (const p of ordered) {
    const norm = normalizeAuthProvider(p);
    if (norm && !result.includes(norm)) result.push(norm);
  }
  return result;
}

export async function getUpstandAuthCalendarProvider(): Promise<CalendarProvider | null> {
  const providers = await getAuthCalendarProviders();
  return providers[0] ?? null;
}

export async function getCalendarConnectionRow(params: {
  workspaceId: string;
  userId: string;
  provider: CalendarProvider;
}): Promise<{ id: string; external_account_email: string | null; token_expires_at: string | null } | null> {
  const { workspaceId, userId, provider } = params;
  const { data, error } = await supabase
    .from('calendar_connections')
    .select('id, external_account_email, token_expires_at')
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId)
    .eq('provider', provider)
    .maybeSingle();

  if (error) return null;
  if (!data) return null;
  return {
    id: data.id,
    external_account_email: data.external_account_email ?? null,
    token_expires_at: data.token_expires_at ? String(data.token_expires_at) : null,
  };
}

async function tryBootstrapCalendarConnectionFromSession(params: {
  workspaceId: string;
  userId: string;
  provider: CalendarProvider;
}): Promise<boolean> {
  const { workspaceId, userId, provider } = params;
  const {
    data: { session },
  } = await supabase.auth.getSession();

  let providerToken = session?.provider_token ?? null;
  let providerRefreshToken = session?.provider_refresh_token ?? null;
  let email = session?.user?.email ?? null;
  // `session.user.app_metadata.provider` uses `azure` for Microsoft.
  let sessionProvider = normalizeAuthProvider(session?.user?.app_metadata?.provider);

  // Fall back to the tokens stashed at the OAuth callback - Supabase drops provider_refresh_token
  // from the session after the first JWT refresh, so by the time a workspace is selected the live
  // session usually no longer carries it.
  if (!providerRefreshToken) {
    const stashed = readStashedProviderTokens();
    if (stashed && normalizeAuthProvider(stashed.provider) === provider) {
      providerToken = providerToken ?? stashed.access_token;
      providerRefreshToken = stashed.refresh_token;
      email = email ?? stashed.email;
      sessionProvider = provider;
    }
  }

  // Nothing to persist.
  if (!providerToken && !providerRefreshToken) return false;
  if (sessionProvider !== provider) return false;

  const approxExpiry = new Date(Date.now() + 55 * 60 * 1000).toISOString();
  // Build the payload conditionally so re-persisting an existing connection is safe:
  //  - We always refresh the access token / expiry / email.
  //  - We ONLY overwrite refresh_token_enc when we actually captured a fresh refresh
  //    token, otherwise an ordinary sign-in (Google returns access-only) would wipe the
  //    good stored token. Omitting the column leaves the existing value untouched on
  //    conflict-update.
  //  - We ONLY clear needs_reconnect when we have that fresh refresh token - clearing it
  //    on an access-only sign-in could mask a genuinely dead stored token and bounce
  //    straight back to needs_reconnect on the next backend refresh.
  const payload: Record<string, unknown> = {
    workspace_id: workspaceId,
    user_id: userId,
    provider,
    access_token_enc: providerToken ?? null,
    token_expires_at: providerToken ? approxExpiry : null,
    external_account_email: email,
    updated_at: new Date().toISOString(),
  };
  if (providerRefreshToken) {
    payload.refresh_token_enc = providerRefreshToken;
    payload.needs_reconnect = false;
  }
  const { error } = await supabase
    .from('calendar_connections')
    .upsert(payload, { onConflict: 'workspace_id,user_id,provider' });

  if (!error) {
    clearStashedProviderTokens();
    // Fire-and-forget: register calendar push watches for the fresh connection (PRD_AUTO_ENROL
    // Phase 1). Server self-gates on the rollout flag; the 15-min reconciler is the fallback.
    void import('@/lib/edge-functions')
      .then(({ invokeEdgeFn }) => invokeEdgeFn('calendar-watch-manage', { action: 'ensure', workspace_id: workspaceId }))
      .catch(() => {});
  }
  return !error;
}

/**
 * Eagerly establish the calendar connection from the just-completed OAuth sign-in, so the user
 * never has to do a separate "Connect calendar" step. No-ops when there's already a connection,
 * when the user didn't sign in with Google/Microsoft, or when no provider tokens are available.
 */
export async function ensureCalendarConnectionFromAuth(params: {
  workspaceId: string;
  userId: string;
}): Promise<void> {
  const provider = await getUpstandAuthCalendarProvider();
  if (!provider) return;
  // Always attempt the bootstrap - it no-ops when there are no provider tokens on the
  // session/stash (i.e. ordinary navigations), and when a row already exists but we DID
  // just capture a fresh token (e.g. a reconnect after revoking access in Google) it
  // re-persists that token and clears any stale needs_reconnect flag. The previous
  // early-return on an existing row meant a re-consent never updated the dead token.
  await tryBootstrapCalendarConnectionFromSession({ ...params, provider });
}

export async function getCalendarConnectionStateForAddMeeting(params: {
  workspaceId: string;
  userId: string;
}): Promise<{ state: CalendarConnectionState; provider: CalendarProvider | null }> {
  const { workspaceId, userId } = params;

  // Source of truth: the user's existing calendar_connections in this workspace - exactly what the
  // Integrations page shows. Keying off the sign-in provider alone breaks for multi-provider
  // accounts (e.g. signed up with email/password, later linked Microsoft → app_metadata.provider
  // stays "email"), so the picker would wrongly think nothing is connected.
  const { data: rows } = await supabase
    .from('calendar_connections')
    .select('provider, needs_reconnect')
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId);

  const connections = (rows || [])
    .map((r) => ({
      provider:
        r.provider === 'google' || r.provider === 'microsoft' ? (r.provider as CalendarProvider) : null,
      needsReconnect: Boolean(r.needs_reconnect),
    }))
    .filter((r): r is { provider: CalendarProvider; needsReconnect: boolean } => r.provider != null);

  const authProviders = await getAuthCalendarProviders();

  if (connections.length > 0) {
    // Prefer a healthy (not needs_reconnect) connection; prefer the sign-in provider on ties;
    // otherwise just take whatever connection exists.
    const preference = [...authProviders, 'google', 'microsoft'] as CalendarProvider[];
    const healthy = connections.filter((c) => !c.needsReconnect);
    const pool = healthy.length > 0 ? healthy : connections;
    const chosen = preference.find((p) => pool.some((c) => c.provider === p)) ?? pool[0].provider;
    return { state: 'CALENDAR_CONNECTED', provider: chosen };
  }

  // No stored connection yet - fall back to the sign-in provider and try to bootstrap one from the
  // just-completed OAuth session (covers the first enrolment immediately after sign-in).
  const provider = authProviders[0] ?? null;
  if (!provider) {
    return { state: 'NONE', provider: null };
  }
  const bootstrapped = await tryBootstrapCalendarConnectionFromSession({ workspaceId, userId, provider });
  if (bootstrapped) {
    const newConn = await getCalendarConnectionRow({ workspaceId, userId, provider });
    if (newConn) {
      return { state: 'CALENDAR_CONNECTED', provider };
    }
  }
  return { state: 'ACCOUNT_ONLY', provider };
}

